import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { api } from '@/lib/api';
import { apiErrorMessage } from '@/lib/clients';
import { WEIGHBRIDGE_TIMING_LABELS, fmtKg, toLocalInput } from '@/lib/missions';
import { newId } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

import type { MissionRotation, WeighbridgeTiming } from '@logistech/ui-shared';

const weight = z.string().trim().min(1, 'Requis').refine((v) => !Number.isNaN(Number(v)) && Number(v) >= 0, 'Poids invalide');

// Aligné sur le schema Zod des rotations (missions-service routes/missions/rotations.ts) ; le net est calculé serveur
const schema = z.object({
  weighedAt: z.string().min(1, 'Date requise'),
  weighbridgeTiming: z.enum(['BEFORE_LOADING', 'AFTER_LOADING', 'BEFORE_UNLOADING', 'AFTER_UNLOADING'], {
    errorMap: () => ({ message: 'Type de pesée requis' }),
  }),
  grossWeightKg: weight,
  tareWeightKg: weight,
  ticketNumber: z.string().max(100),
  notes: z.string().max(5000),
});

type FormData = z.infer<typeof schema>;

const toPayload = (d: FormData, emptyAs: undefined | null) => ({
  weighedAt: new Date(d.weighedAt).toISOString(),
  weighbridgeTiming: d.weighbridgeTiming,
  grossWeightKg: Number(d.grossWeightKg),
  tareWeightKg: Number(d.tareWeightKg),
  ticketNumber: d.ticketNumber.trim() || emptyAs,
  notes: d.notes.trim() || emptyAs,
});

const kgInput = (v: string | number | null | undefined) => (v != null ? String(Number(v)) : '');

const toDefaults = (rotation: MissionRotation | undefined, vehicleTareKg: string | number | null | undefined) => ({
  weighedAt: toLocalInput(rotation?.weighedAt ?? new Date()),
  weighbridgeTiming: (rotation?.weighbridgeTiming ?? undefined) as WeighbridgeTiming,
  grossWeightKg: kgInput(rotation?.grossWeightKg),
  tareWeightKg: kgInput(rotation ? rotation.tareWeightKg : vehicleTareKg),
  ticketNumber: rotation?.ticketNumber ?? '',
  notes: rotation?.notes ?? '',
});

interface Props {
  missionId: string;
  /** Tare du véhicule de la mission, proposée par défaut */
  vehicleTareKg?: string | number | null;
  /** Rotation à modifier ; absente = création */
  rotation?: MissionRotation;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function RotationDialog({ missionId, vehicleTareKg, rotation, open, onOpenChange }: Props) {
  const qc = useQueryClient();
  const isEdit = !!rotation;

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm<FormData>({ resolver: zodResolver(schema), defaultValues: toDefaults(rotation, vehicleTareKg) });

  useEffect(() => {
    if (open) reset(toDefaults(rotation, vehicleTareKg));
  }, [open, rotation, vehicleTareKg, reset]);

  const mutation = useMutation({
    mutationFn: async (d: FormData) => {
      if (isEdit) await api.patch(`/missions/${missionId}/rotations/${rotation.id}`, toPayload(d, null));
      else await api.post(`/missions/${missionId}/rotations`, { id: newId(), ...toPayload(d, undefined) });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['mission', missionId] });
      void qc.invalidateQueries({ queryKey: ['missions'] });
      toast.success(isEdit ? 'Rotation modifiée' : 'Rotation ajoutée');
      onOpenChange(false);
    },
    onError: (err: unknown) => toast.error(apiErrorMessage(err, "Erreur lors de l'enregistrement de la rotation")),
  });

  const [gross, tare, timing, weighedAt] = watch(['grossWeightKg', 'tareWeightKg', 'weighbridgeTiming', 'weighedAt']);
  const hasWeights = gross.trim() !== '' && tare.trim() !== '' && !Number.isNaN(Number(gross)) && !Number.isNaN(Number(tare));
  const net = hasWeights ? Number(gross) - Number(tare) : null;
  const canSave = !!weighedAt && !!timing && net !== null && net >= 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>{isEdit ? `Modifier la rotation n° ${rotation.sequence}` : 'Nouvelle rotation'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit((d) => mutation.mutate(d))} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="rot-weighedAt">Date et heure *</Label>
              <Input id="rot-weighedAt" type="datetime-local" {...register('weighedAt')} />
              {errors.weighedAt && <p className="text-xs text-danger mt-1">{errors.weighedAt.message}</p>}
            </div>
            <div>
              <Label>Type de pesée *</Label>
              <Select value={timing || undefined} onValueChange={(v) => setValue('weighbridgeTiming', v as WeighbridgeTiming, { shouldValidate: true })}>
                <SelectTrigger><SelectValue placeholder="Choisir…" /></SelectTrigger>
                <SelectContent>
                  {Object.entries(WEIGHBRIDGE_TIMING_LABELS).map(([value, label]) => (
                    <SelectItem key={value} value={value}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.weighbridgeTiming && <p className="text-xs text-danger mt-1">{errors.weighbridgeTiming.message}</p>}
            </div>
            <div>
              <Label htmlFor="rot-gross">Poids total sur bascule (kg) *</Label>
              <Input id="rot-gross" type="number" min={0} step={0.1} {...register('grossWeightKg')} placeholder="40000" />
              {errors.grossWeightKg && <p className="text-xs text-danger mt-1">{errors.grossWeightKg.message}</p>}
            </div>
            <div>
              <Label htmlFor="rot-tare">Tare camion (kg) *</Label>
              <Input id="rot-tare" type="number" min={0} step={0.1} {...register('tareWeightKg')} placeholder="15000" />
              {errors.tareWeightKg && <p className="text-xs text-danger mt-1">{errors.tareWeightKg.message}</p>}
            </div>
            <div className="col-span-2">
              {net !== null && net < 0 ? (
                <p className="text-sm text-danger font-medium">Erreur : le poids net ne peut pas être négatif</p>
              ) : (
                <p className="text-sm">
                  Poids net : <span className="font-semibold">{net !== null ? fmtKg(net) : '—'}</span>
                </p>
              )}
            </div>
            <div className="col-span-2">
              <Label htmlFor="rot-ticket">N° de ticket pont bascule</Label>
              <Input id="rot-ticket" {...register('ticketNumber')} />
            </div>
            <div className="col-span-2">
              <Label htmlFor="rot-notes">Notes</Label>
              <textarea
                autoComplete="off"
                id="rot-notes"
                {...register('notes')}
                rows={2}
                className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" type="button" onClick={() => onOpenChange(false)}>Annuler</Button>
            <Button type="submit" disabled={!canSave || mutation.isPending}>
              {mutation.isPending ? 'Enregistrement…' : 'Enregistrer'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
